import type { CSSProperties, ReactNode } from 'react';
import styles from './Chip.module.css';

type ChipProps = {
  label: ReactNode;
  selected: boolean;
  onClick: () => void;
  /** Accent color for the selected state. Pass a CSS custom property
   *  (e.g. 'var(--c-recovery)') or a hex. Defaults to `--signal`. */
  accent?: string;
  accentSoft?: string;
};

/**
 * Interactive pill chip. Used for Step 6 goal-event, time-goal
 * distance, health motivations (multi-select), and social frequency
 * selections.
 *
 * Matches mockup chip pattern (see README line 270+). Accent
 * overridable per coach — e.g. health motivation chips use
 * `--c-recovery` emerald.
 */
export function Chip({ label, selected, onClick, accent, accentSoft }: ChipProps) {
  const style: CSSProperties & Record<string, string> = {};
  if (accent) style['--chip-accent'] = accent;
  if (accentSoft) style['--chip-accent-soft'] = accentSoft;

  return (
    <button
      type="button"
      className={[styles.chip, selected && styles.selected].filter(Boolean).join(' ')}
      onClick={onClick}
      style={style}
      aria-pressed={selected}
    >
      {label}
    </button>
  );
}

/** Flex-wrap container for a set of chips. Use when grouping a row. */
export function ChipGroup({ children }: { children: ReactNode }) {
  return <div className={styles.group}>{children}</div>;
}
